import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Switch } from './ui/switch';
import {
  Loader2, Sparkles, RefreshCw, ExternalLink, Heart, Repeat,
  Edit3, Save, Send, Clock, Trash2, Calendar, Check, X,
  Lightbulb, PenTool, Archive, AlertCircle, TrendingUp,
  BarChart3, Settings, Zap, CheckCircle2, Copy, XCircle, MessageSquareReply
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Success/Error Modal Component
const ResultModal = ({ isOpen, onClose, type, title, message, tweetUrl }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-[#0A0A0A] border border-white/10 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#6B7280] hover:text-[#F3F4F6] transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="text-center">
          {type === 'success' ? (
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-400" />
            </div>
          ) : type === 'copied' ? (
            <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
              <Copy className="w-8 h-8 text-blue-400" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
          )}
          
          <h3 className="text-xl font-semibold text-[#F3F4F6] mb-2">{title}</h3>
          <p className="text-[#6B7280] text-sm mb-6">{message}</p>
          
          {tweetUrl && (
            <a
              href={tweetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF]/10 text-[#00F0FF] rounded-lg border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20 transition-colors mb-4"
            >
              <ExternalLink className="w-4 h-4" />
              View on X
            </a>
          )}
          
          <div className="mt-4">
            <Button
              onClick={onClose}
              className="w-full bg-white/10 text-[#F3F4F6] hover:bg-white/20"
            >
              {type === 'success' ? 'Done' : 'Close'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ComposeTab = ({ getAuthHeaders, twitterConnected, settings }) => {
  // Inspiration state
  const [inspiration, setInspiration] = useState(null);
  const [inspirationLoading, setInspirationLoading] = useState(false);
  const [inspirationError, setInspirationError] = useState(null);
  const [activeSearchTopic, setActiveSearchTopic] = useState(null);

  // Drafts state
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [editedText, setEditedText] = useState('');

  // Queue state
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Scheduling state
  const [schedulingDraft, setSchedulingDraft] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  // Optimal times & Analytics
  const [optimalTimes, setOptimalTimes] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // Settings state
  const [composeSettings, setComposeSettings] = useState({
    auto_post_enabled: false,
    posts_per_day: 3,
    smart_delay_enabled: true
  });
  const [showSettings, setShowSettings] = useState(false);
  
  // Search topics from settings (hardcoded: Thesis match, Watchlist + configurable topics)
  const searchTopics = [
    { id: 'thesis_match', label: 'Thesis match', type: 'hardcoded', icon: '🎯' },
    { id: 'watchlist', label: 'Watchlist', type: 'hardcoded', icon: '👥' },
    ...(settings?.grok_inspiration_topics || ['#AI', '#technology', '#innovation']).map(topic => ({
      id: topic,
      label: topic,
      type: 'configurable',
      icon: topic.startsWith('#') ? '🏷️' : '💡'
    }))
  ];

  // Manual tweet import state
  const [tweetUrl, setTweetUrl] = useState('');
  const [importingTweet, setImportingTweet] = useState(false);
  
  // Response length setting - default to medium (1000 chars)
  const [responseLength, setResponseLength] = useState('medium');
  const responseLengthOptions = [
    { value: 'short', label: 'Short', maxChars: 280 },
    { value: 'medium', label: 'Medium', maxChars: 1000 },
    { value: 'long', label: 'Long', maxChars: 2500 }
  ];

  // Result modal state
  const [resultModal, setResultModal] = useState({
    isOpen: false,
    type: 'success', // 'success', 'error', 'copied'
    title: '',
    message: '',
    tweetUrl: null
  });

  const showResultModal = (type, title, message, tweetUrl = null) => {
    setResultModal({ isOpen: true, type, title, message, tweetUrl });
  };

  const closeResultModal = () => {
    setResultModal(prev => ({ ...prev, isOpen: false }));
  };

  // Import tweet from URL
  const importTweetFromUrl = async () => {
    if (!tweetUrl.trim()) return;
    
    // Extract tweet ID from URL
    const urlMatch = tweetUrl.match(/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/);
    if (!urlMatch) {
      showResultModal('error', 'Invalid URL', 'Please paste a valid X/Twitter post URL (e.g., https://x.com/user/status/123...)');
      return;
    }
    
    const tweetId = urlMatch[1];
    setImportingTweet(true);
    setInspirationError(null);
    setDrafts([]);
    setActiveSearchTopic(null);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/import-tweet/${tweetId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setInspiration(data);
        setTweetUrl(''); // Clear input after successful import
        // Auto-generate drafts with current length setting
        generateDrafts(data);
      } else {
        const error = await res.json();
        setInspirationError(error.detail || 'Failed to import tweet');
      }
    } catch (error) {
      console.error('Failed to import tweet:', error);
      setInspirationError('Network error. Please try again.');
    } finally {
      setImportingTweet(false);
    }
  };

  // Fetch queue on mount
  useEffect(() => {
    fetchQueue();
    fetchOptimalTimes();
    fetchAnalytics();
    fetchComposeSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQueue = async () => {
    setQueueLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/queue`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setQueue(data);
      }
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    } finally {
      setQueueLoading(false);
    }
  };

  const fetchOptimalTimes = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/optimal-times`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setOptimalTimes(data);
      }
    } catch (error) {
      console.error('Failed to fetch optimal times:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/analytics/insights`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchComposeSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/settings`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setComposeSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch compose settings:', error);
    }
  };

  const updateComposeSettings = async (updates) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        const data = await res.json();
        setComposeSettings(data);
      }
    } catch (error) {
      console.error('Failed to update compose settings:', error);
    }
  };

  const findInspiration = async (topicId = null) => {
    setInspirationLoading(true);
    setInspirationError(null);
    setDrafts([]);
    setActiveSearchTopic(topicId);
    
    // Determine source_type and search_topic based on topic clicked
    let sourceType = 'auto';
    let searchTopic = null;
    
    if (topicId === 'thesis_match') {
      sourceType = 'thesis_match';
    } else if (topicId === 'watchlist') {
      sourceType = 'watchlist';
    } else if (topicId) {
      sourceType = 'topic_search';
      searchTopic = topicId;
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/inspiration`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          source_type: sourceType,
          min_engagement: 50,
          max_age_hours: 48,
          search_topic: searchTopic
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setInspiration(data);
        // Auto-generate drafts
        generateDrafts(data);
      } else {
        const error = await res.json();
        setInspirationError(error.detail || 'Failed to find inspiration');
      }
    } catch (error) {
      console.error('Failed to find inspiration:', error);
      setInspirationError('Network error. Please try again.');
    } finally {
      setInspirationLoading(false);
    }
  };

  const generateDrafts = async (insp = inspiration) => {
    if (!insp) return;
    
    // Get max chars from current response length setting
    const maxChars = responseLengthOptions.find(o => o.value === responseLength)?.maxChars || 280;
    
    setDraftsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/drafts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          inspiration_id: insp.inspiration_id,
          inspiration_text: insp.tweet_text,
          author_handle: insp.author_handle,
          topic: insp.topic_tags?.join(', '),
          max_chars: maxChars
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setDrafts(data.drafts || []);
      }
    } catch (error) {
      console.error('Failed to generate drafts:', error);
    } finally {
      setDraftsLoading(false);
    }
  };

  // Clear inspiration and drafts after action
  const clearInspirationAndDrafts = () => {
    setInspiration(null);
    setDrafts([]);
    setInspirationError(null);
    setActiveSearchTopic(null);
  };

  const saveDraft = async (draft, scheduledTime = null, isReply = false) => {
    setActionLoading(`save_${draft.style}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/queue`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          draft_text: draft.text,
          style: draft.style,
          inspiration_id: inspiration?.inspiration_id,
          inspiration_author: inspiration?.author_handle,
          inspiration_tweet_url: inspiration?.tweet_url,
          inspiration_tweet_id: inspiration?.tweet_id,
          scheduled_time: scheduledTime,
          is_reply: isReply
        })
      });
      
      if (res.ok) {
        fetchQueue();
        setSchedulingDraft(null);
        setScheduleDate('');
        setScheduleTime('');
        // Clear inspiration and drafts after successful save
        clearInspirationAndDrafts();
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const scheduleDraft = (draft, index) => {
    setSchedulingDraft({ draft, index });
    // Set default to next optimal time
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    setScheduleDate(tomorrow.toISOString().split('T')[0]);
    setScheduleTime('09:00');
  };

  const confirmSchedule = () => {
    if (schedulingDraft && scheduleDate && scheduleTime) {
      const scheduledTime = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
      saveDraft(schedulingDraft.draft, scheduledTime);
    }
  };

  const postDraft = async (draft, asReply = false) => {
    if (!twitterConnected) {
      // Copy to clipboard instead
      await navigator.clipboard.writeText(draft.text);
      showResultModal('copied', 'Copied to Clipboard', 'Connect your X account to post directly.', null);
      return;
    }
    
    // Check if reply is possible
    if (asReply && !inspiration?.tweet_id) {
      showResultModal('error', 'Cannot Reply', 'No tweet ID available for reply. Try posting instead.', null);
      return;
    }
    
    // Save to queue first, then post
    setActionLoading(asReply ? `reply_${draft.style}` : `post_${draft.style}`);
    try {
      // First save to queue
      const saveRes = await fetch(`${BACKEND_URL}/api/compose/queue`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          draft_text: draft.text,
          style: draft.style,
          inspiration_id: inspiration?.inspiration_id,
          inspiration_author: inspiration?.author_handle,
          inspiration_tweet_url: inspiration?.tweet_url,
          inspiration_tweet_id: inspiration?.tweet_id,
          is_reply: asReply
        })
      });
      
      if (saveRes.ok) {
        const saved = await saveRes.json();
        // Now post it (with reply flag if needed)
        const postRes = await fetch(`${BACKEND_URL}/api/compose/queue/${saved.queue_id}/post?as_reply=${asReply}`, {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders()
        });
        
        if (postRes.ok) {
          const result = await postRes.json();
          const message = asReply 
            ? `Your reply to @${inspiration?.author_handle} has been posted!` 
            : 'Your tweet has been published successfully.';
          showResultModal('success', asReply ? 'Reply Posted!' : 'Posted!', message, result.tweet_url);
          fetchQueue();
          fetchAnalytics();
          // Clear inspiration and drafts after successful post
          clearInspirationAndDrafts();
        } else {
          const error = await postRes.json();
          showResultModal('error', 'Failed to Post', error.detail || 'Something went wrong. Please try again.', null);
        }
      }
    } catch (error) {
      console.error('Failed to post draft:', error);
      showResultModal('error', 'Error', 'Network error. Please check your connection and try again.', null);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteQueueItem = async (queueId) => {
    setActionLoading(`delete_${queueId}`);
    try {
      await fetch(`${BACKEND_URL}/api/compose/queue/${queueId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      fetchQueue();
    } catch (error) {
      console.error('Failed to delete:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const postFromQueue = async (queueId) => {
    if (!twitterConnected) {
      const item = queue.find(q => q.queue_id === queueId);
      if (item) {
        await navigator.clipboard.writeText(item.draft_text);
        showResultModal('copied', 'Copied to Clipboard', 'Connect your X account to post directly.', null);
      }
      return;
    }
    
    setActionLoading(`post_${queueId}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/compose/queue/${queueId}/post`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const result = await res.json();
        showResultModal('success', 'Posted!', 'Your tweet has been published successfully.', result.tweet_url);
        fetchQueue();
        fetchAnalytics();
      } else {
        const error = await res.json();
        showResultModal('error', 'Failed to Post', error.detail || 'Something went wrong. Please try again.', null);
      }
    } catch (error) {
      console.error('Failed to post:', error);
      showResultModal('error', 'Error', 'Network error. Please check your connection and try again.', null);
    } finally {
      setActionLoading(null);
    }
  };

  const rescheduleQueueItem = async (queueId, newTime) => {
    setActionLoading(`schedule_${queueId}`);
    try {
      await fetch(`${BACKEND_URL}/api/compose/queue/${queueId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ scheduled_time: newTime })
      });
      fetchQueue();
    } catch (error) {
      console.error('Failed to reschedule:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const startEditing = (draft, index) => {
    setEditingDraft(index);
    setEditedText(draft.text);
  };

  const saveEdit = () => {
    if (editingDraft !== null) {
      const newDrafts = [...drafts];
      newDrafts[editingDraft] = {
        ...newDrafts[editingDraft],
        text: editedText,
        char_count: editedText.length
      };
      setDrafts(newDrafts);
      setEditingDraft(null);
      setEditedText('');
    }
  };

  const cancelEdit = () => {
    setEditingDraft(null);
    setEditedText('');
  };

  const getStyleLabel = (style) => {
    switch (style) {
      case 'attribution': return '💬 Attribution';
      case 'building_on': return '🔗 Building On';
      case 'original': return '💡 Original Insight';
      default: return style;
    }
  };

  const getStyleColor = (style) => {
    switch (style) {
      case 'attribution': return 'text-cyan-400 border-cyan-400/30 bg-cyan-400/10';
      case 'building_on': return 'text-purple-400 border-purple-400/30 bg-purple-400/10';
      case 'original': return 'text-amber-400 border-amber-400/30 bg-amber-400/10';
      default: return 'text-gray-400 border-gray-400/30 bg-gray-400/10';
    }
  };

  const getThesisFitColor = (fit) => {
    switch (fit) {
      case 'HIGH': return 'text-green-400 border-green-400/30';
      case 'MEDIUM': return 'text-yellow-400 border-yellow-400/30';
      case 'LOW': return 'text-red-400 border-red-400/30';
      default: return 'text-gray-400 border-gray-400/30';
    }
  };

  const formatScheduledTime = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with Settings Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#F3F4F6]">Content Engine</h2>
          <p className="text-sm text-[#6B7280]">Create on-brand tweets with AI assistance</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSettings(!showSettings)}
          className={`border-white/10 ${showSettings ? 'bg-white/10 text-[#F3F4F6]' : 'text-[#6B7280]'}`}
        >
          <Settings className="w-4 h-4 mr-1" />
          Settings
        </Button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card className="bg-white/[0.02] border-white/10">
          <CardContent className="pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#F3F4F6]">Auto-Post Scheduled Tweets</p>
                <p className="text-xs text-[#6B7280]">Automatically post tweets at their scheduled time</p>
              </div>
              <Switch
                checked={composeSettings.auto_post_enabled}
                onCheckedChange={(checked) => updateComposeSettings({ auto_post_enabled: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#F3F4F6]">Smart Delay</p>
                <p className="text-xs text-[#6B7280]">Delay posts during major trending events</p>
              </div>
              <Switch
                checked={composeSettings.smart_delay_enabled}
                onCheckedChange={(checked) => updateComposeSettings({ smart_delay_enabled: checked })}
              />
            </div>
            {!twitterConnected && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <AlertCircle className="w-4 h-4 text-yellow-400" />
                <span className="text-xs text-yellow-400">Connect X account to enable auto-posting</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Analytics Summary */}
      {analytics && analytics.total_posts > 0 && (
        <Card className="bg-white/[0.02] border-white/10">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-[#F3F4F6]">Performance Insights</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 rounded-lg bg-white/[0.02]">
                <p className="text-2xl font-bold text-[#00F0FF]">{analytics.total_posts}</p>
                <p className="text-xs text-[#6B7280]">Posts</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/[0.02]">
                <p className="text-2xl font-bold text-green-400">{analytics.avg_engagement?.toFixed(1) || 0}</p>
                <p className="text-xs text-[#6B7280]">Avg Engagement</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/[0.02]">
                <p className="text-2xl font-bold text-purple-400">{analytics.best_style || '-'}</p>
                <p className="text-xs text-[#6B7280]">Best Style</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-white/[0.02]">
                <p className="text-2xl font-bold text-amber-400">{analytics.best_time || '-'}</p>
                <p className="text-xs text-[#6B7280]">Best Time</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 1: Find Inspiration */}
      <Card className="bg-white/[0.02] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-[#F3F4F6] text-lg">
            <Lightbulb className="w-5 h-5 text-amber-400" />
            Find Inspiration
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Paste Tweet URL Input - at the top */}
          <div className="flex gap-2 mb-4">
            <Input
              value={tweetUrl}
              onChange={(e) => setTweetUrl(e.target.value)}
              placeholder="Paste a tweet URL (https://x.com/user/status/...)"
              className="bg-white/5 border-white/10 text-[#F3F4F6] flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && tweetUrl.trim()) {
                  importTweetFromUrl();
                }
              }}
            />
            <Button
              onClick={importTweetFromUrl}
              disabled={importingTweet || !tweetUrl.trim()}
              className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20"
            >
              {importingTweet ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Import'
              )}
            </Button>
          </div>

          {/* Divider with "or" */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-white/10"></div>
            <span className="text-xs text-[#6B7280]">or discover via</span>
            <div className="flex-1 h-px bg-white/10"></div>
          </div>

          {/* Search Topic Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            {searchTopics.map((topic) => (
              <Button
                key={topic.id}
                onClick={() => findInspiration(topic.id)}
                disabled={inspirationLoading}
                variant="outline"
                size="sm"
                className={`
                  ${activeSearchTopic === topic.id 
                    ? 'bg-[#00F0FF]/20 border-[#00F0FF]/50 text-[#00F0FF]' 
                    : topic.type === 'hardcoded'
                      ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'
                      : 'border-white/20 text-[#6B7280] hover:text-[#F3F4F6] hover:bg-white/5'
                  }
                `}
              >
                {inspirationLoading && activeSearchTopic === topic.id ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <span className="mr-1">{topic.icon}</span>
                )}
                {topic.label}
              </Button>
            ))}
          </div>

          {inspirationError && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 mb-4">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">{inspirationError}</span>
            </div>
          )}
          
          {!inspiration && !inspirationLoading && !inspirationError && (
            <div className="text-center py-8">
              <Lightbulb className="w-12 h-12 text-[#6B7280]/30 mx-auto mb-3" />
              <p className="text-[#6B7280] text-sm">Click a topic above to find tweets worth engaging with</p>
              <p className="text-[#6B7280]/60 text-xs mt-1">Powered by Grok&apos;s live search</p>
            </div>
          )}
          
          {inspirationLoading && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin mx-auto mb-3" />
              <p className="text-[#6B7280] text-sm">Searching for inspiration...</p>
            </div>
          )}
          
          {inspiration && !inspirationLoading && (
            <div className="space-y-3">
              {/* Source badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-xs text-[#6B7280] border-white/10">
                  {inspiration.source_type?.replace('_', ' ')}
                </Badge>
                <Badge variant="outline" className={`text-xs ${getThesisFitColor(inspiration.thesis_fit)}`}>
                  Thesis Fit: {inspiration.thesis_fit}
                </Badge>
                {inspiration.topic_tags?.map((tag, i) => (
                  <Badge key={i} variant="outline" className="text-xs text-[#6B7280] border-white/10">
                    #{tag}
                  </Badge>
                ))}
              </div>
              
              {/* Author and tweet */}
              <div className="p-4 rounded-lg bg-white/[0.03] border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <a
                    href={inspiration.tweet_url || `https://x.com/${inspiration.author_handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[#00F0FF] hover:underline flex items-center gap-1"
                  >
                    @{inspiration.author_handle}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <div className="flex items-center gap-3 text-xs text-[#6B7280]">
                    <span className="flex items-center gap-1">
                      <Heart className="w-3 h-3" /> {inspiration.likes}
                    </span>
                    <span className="flex items-center gap-1">
                      <Repeat className="w-3 h-3" /> {inspiration.retweets}
                    </span>
                  </div>
                </div>
                <p className="text-[#F3F4F6] leading-relaxed whitespace-pre-wrap">{inspiration.tweet_text}</p>
                {/* Truncation warning - only show if Twitter NOT connected */}
                {inspiration.may_be_truncated && !twitterConnected && (
                  <div className="mt-3 p-2 rounded bg-amber-500/10 border border-amber-500/30">
                    <p className="text-amber-400 text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      This tweet may be truncated. Connect your X account in Settings for full text.
                      <a
                        href={inspiration.tweet_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00F0FF] hover:underline ml-1"
                      >
                        View full tweet →
                      </a>
                    </p>
                  </div>
                )}
                {/* View full tweet link - show if text contains ellipsis but no warning */}
                {inspiration.tweet_text?.includes('…') && (twitterConnected || !inspiration.may_be_truncated) && (
                  <a
                    href={inspiration.tweet_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00F0FF] text-sm hover:underline mt-2 inline-block"
                  >
                    View full tweet on X →
                  </a>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Draft Options */}
      {(inspiration || draftsLoading) && (
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="flex items-center gap-2 text-[#F3F4F6] text-lg">
                <PenTool className="w-5 h-5 text-purple-400" />
                Draft Options
              </CardTitle>
              <div className="flex items-center gap-2">
                {/* Response Length Selector */}
                <div className="flex items-center gap-1 bg-white/[0.02] rounded-md border border-white/10 p-1">
                  {responseLengthOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setResponseLength(option.value)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        responseLength === option.value
                          ? 'bg-[#00F0FF]/20 text-[#00F0FF]'
                          : 'text-[#6B7280] hover:text-[#F3F4F6] hover:bg-white/5'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {/* Regenerate Button */}
                <Button
                  onClick={() => generateDrafts()}
                  disabled={draftsLoading || !inspiration}
                  variant="outline"
                  size="sm"
                  className="border-white/10 text-[#6B7280] hover:text-[#F3F4F6]"
                >
                  {draftsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-1" /> Regenerate</>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {draftsLoading && (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
                <p className="text-[#6B7280] text-sm">Crafting drafts with Gemini...</p>
              </div>
            )}
            
            {!draftsLoading && drafts.length === 0 && (
              <div className="text-center py-8">
                <PenTool className="w-12 h-12 text-[#6B7280]/30 mx-auto mb-3" />
                <p className="text-[#6B7280] text-sm">Drafts will appear here after finding inspiration</p>
              </div>
            )}
            
            {!draftsLoading && drafts.length > 0 && (
              <div className="space-y-3">
                {drafts.map((draft, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-lg bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <Badge className={`${getStyleColor(draft.style)} text-xs mb-2`}>
                        {getStyleLabel(draft.style)}
                      </Badge>
                      <span className={`text-xs font-mono ${draft.char_count > 280 ? 'text-red-400' : 'text-[#6B7280]'}`}>
                        {draft.char_count}/280
                      </span>
                    </div>
                    
                    {/* Scheduling UI */}
                    {schedulingDraft?.index === index && (
                      <div className="mb-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                        <p className="text-xs text-blue-400 mb-2">Schedule this tweet:</p>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="date"
                            value={scheduleDate}
                            onChange={(e) => setScheduleDate(e.target.value)}
                            className="bg-white/5 border-white/10 text-[#F3F4F6] text-sm h-8"
                          />
                          <Input
                            type="time"
                            value={scheduleTime}
                            onChange={(e) => setScheduleTime(e.target.value)}
                            className="bg-white/5 border-white/10 text-[#F3F4F6] text-sm h-8 w-28"
                          />
                          <Button size="sm" onClick={confirmSchedule} className="bg-blue-500 text-white h-8">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setSchedulingDraft(null)} className="text-[#6B7280] h-8">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        {optimalTimes && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            <span className="text-[10px] text-[#6B7280]">Optimal times:</span>
                            {optimalTimes.times?.map((t, i) => (
                              <button
                                key={i}
                                onClick={() => setScheduleTime(t.time)}
                                className="text-[10px] text-blue-400 hover:underline"
                              >
                                {t.time} ({t.label})
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {editingDraft === index ? (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <textarea
                          key={`edit-${index}`}
                          value={editedText}
                          onChange={(e) => {
                            console.log('onChange:', e.target.value);
                            setEditedText(e.target.value);
                          }}
                          className="w-full min-h-[100px] rounded-md bg-white/5 border border-white/10 text-[#F3F4F6] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                          autoFocus
                        />
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-mono ${editedText.length > 280 ? 'text-red-400' : 'text-[#6B7280]'}`}>
                            {editedText.length}/280
                          </span>
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-[#6B7280]">
                              <X className="w-4 h-4" />
                            </Button>
                            <Button size="sm" onClick={saveEdit} className="bg-green-500/20 text-green-400">
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-[#F3F4F6] text-sm leading-relaxed mb-3">{draft.text}</p>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEditing(draft, index)}
                            className="text-[#6B7280] hover:text-[#F3F4F6]"
                          >
                            <Edit3 className="w-4 h-4 mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => saveDraft(draft)}
                            disabled={actionLoading === `save_${draft.style}`}
                            className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                          >
                            {actionLoading === `save_${draft.style}` ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><Save className="w-4 h-4 mr-1" /> Save</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => scheduleDraft(draft, index)}
                            className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          >
                            <Calendar className="w-4 h-4 mr-1" /> Schedule
                          </Button>
                          {/* Reply button - only show if we have a tweet ID to reply to */}
                          {inspiration?.tweet_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => postDraft(draft, true)}
                              disabled={actionLoading === `reply_${draft.style}`}
                              className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                            >
                              {actionLoading === `reply_${draft.style}` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><MessageSquareReply className="w-4 h-4 mr-1" /> Reply</>
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => postDraft(draft, false)}
                            disabled={actionLoading === `post_${draft.style}`}
                            className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20"
                          >
                            {actionLoading === `post_${draft.style}` ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><Send className="w-4 h-4 mr-1" /> {twitterConnected ? 'Post' : 'Copy'}</>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Section 3: Content Queue */}
      <Card className="bg-white/[0.02] border-white/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-[#F3F4F6] text-lg">
              <Archive className="w-5 h-5 text-green-400" />
              Content Queue
              {queue.length > 0 && (
                <Badge className="bg-green-500/20 text-green-400 text-xs ml-2">{queue.length}</Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              {composeSettings.auto_post_enabled && (
                <Badge className="bg-green-500/10 text-green-400 text-[10px]">
                  <Zap className="w-3 h-3 mr-1" /> Auto-Post On
                </Badge>
              )}
              {optimalTimes && (
                <div className="flex items-center gap-1 text-xs text-[#6B7280]">
                  <Clock className="w-3 h-3" />
                  Next optimal: {optimalTimes.next_optimal}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {queueLoading && (
            <div className="text-center py-6">
              <Loader2 className="w-6 h-6 text-[#6B7280] animate-spin mx-auto" />
            </div>
          )}
          
          {!queueLoading && queue.length === 0 && (
            <div className="text-center py-8">
              <Archive className="w-12 h-12 text-[#6B7280]/30 mx-auto mb-3" />
              <p className="text-[#6B7280] text-sm">No drafts saved yet</p>
              <p className="text-[#6B7280]/60 text-xs mt-1">Save or schedule drafts above to build your content queue</p>
            </div>
          )}
          
          {!queueLoading && queue.length > 0 && (
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-2">
                {queue.map((item) => (
                  <div
                    key={item.queue_id}
                    className="p-3 rounded-lg bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge className={`${getStyleColor(item.style)} text-[10px]`}>
                            {getStyleLabel(item.style)}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              item.status === 'scheduled' ? 'text-blue-400 border-blue-400/30' :
                              item.status === 'posted' ? 'text-green-400 border-green-400/30' :
                              item.status === 'failed' ? 'text-red-400 border-red-400/30' :
                              'text-[#6B7280] border-white/10'
                            }`}
                          >
                            {item.status}
                          </Badge>
                          {item.inspiration_author && (
                            <span className="text-[10px] text-[#6B7280]">
                              re: @{item.inspiration_author}
                            </span>
                          )}
                        </div>
                        <p className="text-[#F3F4F6] text-sm line-clamp-2">{item.draft_text}</p>
                        {item.scheduled_time && (
                          <div className="flex items-center gap-1 mt-1 text-[10px] text-blue-400">
                            <Calendar className="w-3 h-3" />
                            {formatScheduledTime(item.scheduled_time)}
                          </div>
                        )}
                        {item.post_engagement && (
                          <div className="flex items-center gap-3 mt-1 text-[10px] text-[#6B7280]">
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" /> {item.post_engagement.likes}
                            </span>
                            <span className="flex items-center gap-1">
                              <Repeat className="w-3 h-3" /> {item.post_engagement.retweets}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteQueueItem(item.queue_id)}
                          disabled={actionLoading === `delete_${item.queue_id}`}
                          className="text-red-400 hover:bg-red-500/10 p-1 h-auto"
                        >
                          {actionLoading === `delete_${item.queue_id}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                        {item.status !== 'posted' && (
                          <Button
                            size="sm"
                            onClick={() => postFromQueue(item.queue_id)}
                            disabled={actionLoading === `post_${item.queue_id}`}
                            className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20 p-1 h-auto"
                          >
                            {actionLoading === `post_${item.queue_id}` ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Result Modal */}
      <ResultModal
        isOpen={resultModal.isOpen}
        onClose={closeResultModal}
        type={resultModal.type}
        title={resultModal.title}
        message={resultModal.message}
        tweetUrl={resultModal.tweetUrl}
      />
    </div>
  );
};

export default ComposeTab;
